import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Image as ImageIcon, Send, Download, Copy, Trash2, RefreshCw } from 'lucide-react';
import adminFetch from '../../services/adminFetch';
import { config } from '../../config';
import './ModelTrialPage.css';

const STATUS_MAP = {
  pending: { label: '排队中', cls: 'pending' },
  processing: { label: '处理中', cls: 'processing' },
  done: { label: '完成', cls: 'done' },
  failed: { label: '失败', cls: 'failed' },
};

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('zh-CN', { hour12: false });
}

function videoFullUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = config.STATIC_BASE_URL || '';
  return `${base}${path}`;
}

export default function ModelTrialPage() {
  // Form state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [dragging, setDragging] = useState(false);

  // Current job
  const [currentJob, setCurrentJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  // History
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const pageSize = 10;

  // Preview modal
  const [previewUrl, setPreviewUrl] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (statusFilter) params.set('status', statusFilter);
      const res = await adminFetch(`/admin/model-trial/history?${params}`);
      if (!res.ok) return;
      const data = await res.json();
      setHistory(data.items || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
  }, [page, statusFilter]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (requestId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await adminFetch(`/admin/model-trial/status/${requestId}`);
        if (!res.ok) return;
        const data = await res.json();
        setCurrentJob((prev) => ({ ...prev, ...data }));
        if (data.status === 'done' || data.status === 'failed') {
          stopPolling();
          loadHistory();
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  useEffect(() => () => stopPolling(), []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith('image/')) {
      setImageFile(f);
      setImagePreview(URL.createObjectURL(f));
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setImageFile(f);
      setImagePreview(URL.createObjectURL(f));
    }
  };

  const fileInputRef = useRef(null);

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!imageFile) { alert('请上传图片'); return; }
    if (!prompt.trim()) { alert('请输入提示词'); return; }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('prompt', prompt.trim());

    try {
      const res = await adminFetch('/admin/model-trial/generate', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');

      setCurrentJob({
        request_id: data.request_id,
        status: data.status || 'pending',
        prompt: prompt.trim(),
        created_at: data.created_at,
      });
      startPolling(data.request_id);
    } catch (e) {
      alert('提交失败: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('确认删除此任务及文件？')) return;
    try {
      const res = await adminFetch(`/admin/model-trial/jobs/${requestId}`, { method: 'DELETE' });
      if (res.ok) loadHistory();
    } catch { /* ignore */ }
  };

  const handleCopyUrl = (url) => {
    const full = videoFullUrl(url);
    if (full) navigator.clipboard.writeText(full).then(() => alert('已复制'));
  };

  const totalPages = Math.ceil(total / pageSize);

  const progressPct = currentJob?.status === 'done' ? 100 : currentJob?.status === 'failed' ? 100 : currentJob?.status === 'processing' ? 60 : 20;

  return (
    <div className="model-trial-page">
      <div className="mt-header">
        <h1>图生视频</h1>
        <p>上传图片并输入提示词，调用 AI 模型生成视频</p>
      </div>

      {/* Form */}
      <div className="mt-form">
        <div className="mt-upload-row">
          <div
            className={`mt-dropzone ${dragging ? 'dragging' : ''} ${imagePreview ? 'has-image' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => { if (!imagePreview) document.getElementById('mt-file-input')?.click(); }}
          >
            {imagePreview ? (
              <>
                <img src={imagePreview} alt="" className="mt-dropzone-preview" />
                <button className="mt-dropzone-clear" onClick={(e) => { e.stopPropagation(); clearImage(); }}>x</button>
              </>
            ) : (
              <>
                <Upload size={32} className="mt-dropzone-icon" />
                <span>拖拽或点击上传图片</span>
                <span style={{ fontSize: 12, color: '#bbb' }}>JPEG / PNG / WebP，最大 25MB</span>
              </>
            )}
            <input id="mt-file-input" ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} hidden />
          </div>
        </div>

        <div className="mt-prompt-area">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="描述你想生成的视频内容，例如：女孩开心地转圈，裙摆飘起"
            rows={3}
          />
        </div>

        <div className="mt-submit-row">
          <button className="mt-submit-btn" onClick={handleSubmit} disabled={submitting || !imageFile || !prompt.trim()}>
            {submitting ? '提交中...' : <><Send size={14} style={{ marginRight: 6, verticalAlign: -2 }} />生成视频</>}
          </button>
        </div>
      </div>

      {/* Current job result */}
      {currentJob && (
        <div className="mt-result">
          <h3 className="mt-result-title">生成结果</h3>
          <div className="mt-result-progress">
            <div className="mt-result-bar">
              <div
                className={`mt-result-fill ${currentJob.status === 'done' ? 'done' : ''} ${currentJob.status === 'failed' ? 'failed' : ''}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="mt-result-status">
              {STATUS_MAP[currentJob.status]?.label || currentJob.status}
            </span>
          </div>

          {currentJob.status === 'done' && currentJob.output_video_url && (
            <div className="mt-result-video-wrap">
              <video
                src={videoFullUrl(currentJob.output_video_url)}
                controls
                className="mt-result-video"
              />
              <div className="mt-result-actions">
                <a
                  href={videoFullUrl(currentJob.output_video_url)}
                  download
                  className="btn-sm btn-primary"
                  style={{ textDecoration: 'none' }}
                >
                  <Download size={14} style={{ verticalAlign: -2 }} /> 下载
                </a>
                <button className="btn-sm" onClick={() => handleCopyUrl(currentJob.output_video_url)}>
                  <Copy size={14} style={{ verticalAlign: -2 }} /> 复制链接
                </button>
              </div>
            </div>
          )}

          {currentJob.status === 'failed' && currentJob.error_message && (
            <div className="mt-result-error">{currentJob.error_message}</div>
          )}
        </div>
      )}

      {/* History */}
      <div className="mt-history">
        <div className="mt-history-header">
          <h3 className="mt-history-title">历史记录 ({total})</h3>
          <div className="mt-history-filter">
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">全部状态</option>
              <option value="pending">排队中</option>
              <option value="processing">处理中</option>
              <option value="done">完成</option>
              <option value="failed">失败</option>
            </select>
            <button className="btn-sm" onClick={loadHistory}><RefreshCw size={13} style={{ verticalAlign: -1 }} /></button>
          </div>
        </div>

        {history.length === 0 ? (
          <p style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>暂无记录</p>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>时间</th>
                <th>Prompt</th>
                <th>状态</th>
                <th>日志</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.request_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(item.created_at)}</td>
                  <td className="mt-prompt-cell">
                    <span className="mt-prompt-text" title={item.prompt}>{item.prompt}</span>
                  </td>
                  <td>
                    <span className={`mt-status-tag ${STATUS_MAP[item.status]?.cls || ''}`}>
                      {STATUS_MAP[item.status]?.label || item.status}
                    </span>
                  </td>
                  <td className="mt-log-cell">
                    {item.status === 'failed' && item.error_message ? (
                      <span className="mt-log-badge" title={item.error_message}>失败日志</span>
                    ) : item.status === 'done' ? (
                      <span style={{ color: '#52c41a' }}>&#10003;</span>
                    ) : (
                      <span style={{ color: '#bbb' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="mt-actions-cell">
                      {item.status === 'done' && item.output_video_url && (
                        <>
                          <button className="btn-sm btn-primary" onClick={() => setPreviewUrl(videoFullUrl(item.output_video_url))}>
                            <ImageIcon size={13} style={{ verticalAlign: -1 }} /> 查看
                          </button>
                          <a href={videoFullUrl(item.output_video_url)} download className="btn-sm" style={{ textDecoration: 'none' }}>
                            <Download size={13} style={{ verticalAlign: -1 }} />
                          </a>
                        </>
                      )}
                      <button className="btn-sm btn-danger" onClick={() => handleDelete(item.request_id)}>
                        <Trash2 size={13} style={{ verticalAlign: -1 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="mt-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一页</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</button>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewUrl && (
        <div className="modal-overlay" onClick={() => setPreviewUrl(null)}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setPreviewUrl(null)}>x</button>
            <video src={previewUrl} controls autoPlay className="preview-video" />
          </div>
        </div>
      )}
    </div>
  );
}
